// Gelen e-Belgeler (inbox) — fatura veya irsaliye
// Props:
//   docGroup: 'invoice' veya 'waybill'
//   title:    sayfa başlığı
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import { Button, Modal, FilterBar, ListPageHeader, ActionsMenu } from '../components/ui';

const STATUS_COLORS = {
  received:  { bg: 'rgba(59,130,246,0.15)', clr: 'var(--info)' },
  pending:   { bg: 'rgba(245,158,11,0.15)', clr: '#f59e0b' },
  accepted:  { bg: 'rgba(16,185,129,0.15)', clr: 'var(--success)' },
  rejected:  { bg: 'rgba(239,68,68,0.15)',  clr: 'var(--danger)' },
  converted: { bg: 'rgba(99,102,241,0.15)', clr: '#6366f1' },
};
const STATUS_LABELS = {
  received:'Alındı', pending:'Bekliyor', accepted:'Kabul Edildi', rejected:'Reddedildi', converted:'Faturaya Dönüştü',
};
const RESPONSE_LABELS = { accepted:'Kabul', rejected:'Red' };
const DOC_TYPE_LABELS = {
  eFatura:'e-Fatura', eArsiv:'e-Arşiv', eIhracat:'e-İhracat', eIrsaliye:'e-İrsaliye',
};
// Hızlı Bilişim / GİB İngilizce → Türkçe etiket
const GIB_LABELS_TR = {
  succeed:'Başarılı', succeeded:'Başarılı', success:'Başarılı',
  approved:'Onaylandı', accepted:'Kabul Edildi',
  rejected:'Reddedildi', refused:'Reddedildi',
  failed:'Başarısız', error:'Hata',
  pending:'Bekliyor', waiting:'Bekliyor', queued:'Sırada',
  created:'Oluşturuldu', submitted:'GİB\'e Gönderildi', processed:'İşlendi',
  cancelled:'İptal Edildi', canceled:'İptal Edildi',
  delivered:'Teslim Edildi', sent:'Gönderildi',
  unknown:'Bilinmiyor',
};
const trGib = (s) => {
  if (!s) return '-';
  const k = String(s).toLowerCase().trim();
  return GIB_LABELS_TR[k] || s;
};

const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => s ? new Date(s).toLocaleString('tr-TR') : '';
const fmtDateOnly = (s) => s ? new Date(s).toLocaleDateString('tr-TR') : '';

// payload_json'dan ek alanlar çek
const parsePayload = (r) => {
  if (!r) return {};
  if (r._parsed) return r._parsed;
  try {
    const pj = typeof r.payload_json === 'string' ? JSON.parse(r.payload_json) : (r.payload_json || {});
    r._parsed = pj;
    return pj;
  } catch { return {}; }
};

// İnvoiceTypeCode (SATIS/IADE) + ProfileId (TEMELFATURA/TICARIFATURA)
const getInvoiceType = (r) => {
  const pj = parsePayload(r);
  return pj.DocumentTypeCode || pj.InvoiceTypeCode || r.doc_type === 'eIrsaliye' ? 'SEVK' : (pj.DocumentTypeCode || 'SATIS');
};
const getScenario = (r) => {
  const pj = parsePayload(r);
  return pj.ProfileId || pj.ProfileID || '-';
};

// KDV (TaxTotal) — payload'dan oran:tutar formatı
const getKdv = (r) => {
  const pj = parsePayload(r);
  const total = parseFloat(pj.TaxTotal || pj.taxTotal || 0) || 0;
  if (total === 0) return null;
  // Genel oran %20 — gerçek breakdown payload'da yok, basit gösterim
  return total;
};

const getCreationDate = (r) => {
  const pj = parsePayload(r);
  return pj.CreatedDate || pj.CreationDate || r.received_at;
};

export default function EDocumentInboxPage({ docGroup = 'invoice', title = 'Gelen e-Belgeler' }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '' });
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [detail, setDetail] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [viewerHtml, setViewerHtml] = useState(null); // { title, html }
  const [viewerLoading, setViewerLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, doc_group: docGroup };
      const r = await api.get('/e-documents/inbox/list', { params });
      if (r.data.success) setList(r.data.data || []);
    } finally { setLoading(false); }
  }, [filters, docGroup]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    const r = await api.get('/e-documents/inbox/' + row.id);
    if (r.data.success) setDetail(r.data.data);
  };

  const syncInbox = async (days = 30) => {
    if (syncing) return;
    setSyncing(true); setSyncResult(null);
    try {
      const r = await api.post('/e-documents/inbox/sync', { days });
      if (r.data.success) {
        const d = r.data.data;
        setSyncResult({
          type: 'success',
          text: `Son ${days} gün → ${d.total_received} belge alındı · ${d.inserted} yeni · ${d.skipped} zaten kayıtlı`,
        });
        await load();
        // 5 saniye sonra mesajı kaldır
        setTimeout(() => setSyncResult(null), 5000);
      } else {
        setSyncResult({ type: 'error', text: r.data.message || 'Senkronizasyon başarısız' });
      }
    } catch (e) {
      setSyncResult({ type: 'error', text: e.response?.data?.message || e.message });
    } finally { setSyncing(false); }
  };

  const downloadFile = async (row, format) => {
    if (!row.document_uuid) { alert('Bu belgenin UUID bilgisi yok'); return; }
    try {
      const resp = await api.get(`/e-documents/inbox/${row.id}/view?format=${format}`, {
        responseType: 'blob', transformResponse: x => x,
      });
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data]);
      if (blob.type && blob.type.includes('application/json')) {
        const txt = await blob.text();
        try { const j = JSON.parse(txt); alert(j.message || 'İndirme başarısız'); }
        catch { alert('İndirme başarısız'); }
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (row.document_number || row.document_uuid) + '.' + format.toLowerCase();
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      const data = e.response?.data;
      if (data instanceof Blob) {
        const txt = await data.text();
        try { const j = JSON.parse(txt); alert(j.message || 'İndirme hatası'); }
        catch { alert('İndirme hatası'); }
      } else {
        alert(e.response?.data?.message || e.message);
      }
    }
  };

  const viewInvoice = async (row) => {
    if (!row.document_uuid) { alert('Bu belgenin UUID bilgisi yok, görüntülenemez'); return; }
    setViewerLoading(true);
    try {
      const r = await api.get('/e-documents/inbox/' + row.id + '/view?format=HTML', { responseType: 'text', transformResponse: x => x });
      if (typeof r.data === 'string' && r.data.length > 0 && r.data.trim().startsWith('<')) {
        setViewerHtml({ title: row.document_number || row.document_uuid, html: r.data });
      } else {
        // JSON error response gelebilir
        try { const j = JSON.parse(r.data); alert(j.message || 'Görüntüleme başarısız'); }
        catch { alert('Görüntüleme başarısız'); }
      }
    } catch (e) {
      let msg = e.message;
      const d = e.response?.data;
      if (typeof d === 'string') {
        try { msg = JSON.parse(d).message || msg; } catch { if (d) msg = d.substring(0, 500); }
      } else if (d?.message) {
        msg = d.message;
      }
      alert(msg);
    } finally { setViewerLoading(false); }
  };

  const respond = async (row, response_status) => {
    const verb = response_status === 'accepted' ? 'kabul' : 'red';
    if (!window.confirm(`Bu belge ${verb} edilecek. Onaylıyor musunuz?`)) return;
    try {
      const r = await api.post('/e-documents/inbox/' + row.id + '/respond', { response_status });
      if (r.data.success) { load(); setDetail(null); }
    } catch (e) {
      alert(e.response?.data?.message || 'Hata');
    }
  };

  // İtiraz / İade — GİB'e iletilecek formal yanıt
  // ObjectDocument'a giden uygulama yanıtı
  const objection = async (row, kind) => {
    // kind: 'itiraz' | 'iade'
    const isReturn = kind === 'iade';
    const label = isReturn ? 'İade' : 'İtiraz';
    const reason = window.prompt(`${label} sebebini yazınız (zorunlu):`);
    if (!reason || reason.trim().length < 3) return;
    if (!window.confirm(`Bu belge için "${label}" işlemi GİB'e iletilecek.\nSebep: ${reason}\n\nDevam edilsin mi?`)) return;
    try {
      const r = await api.post('/e-documents/inbox/' + row.id + '/object', { kind, reason });
      if (r.data.success) {
        alert(`${label} GİB'e başarıyla iletildi`);
        load(); setDetail(null);
      } else {
        alert(r.data.message || 'İşlem başarısız');
      }
    } catch (e) {
      alert(e.response?.data?.message || 'Hata');
    }
  };

  // İade faturası oluştur — gelen alış faturasını ters çevirip satış faturası taslağı üret (yakında)
  const createReturnInvoice = (row) => {
    alert('İade faturası oluşturma özelliği yakında eklenecek. Bu sırada İade işlemini "İtiraz Et" üzerinden bildirebilirsiniz.');
  };

  const printInvoice = async (row) => {
    if (!row.document_uuid) { alert('Bu belgenin UUID bilgisi yok'); return; }
    try {
      const r = await api.get('/e-documents/inbox/' + row.id + '/view?format=HTML', { responseType: 'text', transformResponse: x => x });
      if (typeof r.data !== 'string' || !r.data.trim().startsWith('<')) { alert('Yazdırma içeriği alınamadı'); return; }
      const w = window.open('', '_blank', 'width=900,height=1100');
      if (!w) { alert('Popup engellendi'); return; }
      w.document.write(r.data);
      w.document.close();
      w.focus();
      setTimeout(() => { try { w.print(); } catch (_) {} }, 400);
    } catch (e) { alert(e.response?.data?.message || e.message); }
  };

  const emailInvoice = async (row) => {
    if (!row.document_uuid) { alert('Bu belgenin UUID bilgisi yok'); return; }
    try {
      const st = await api.get('/e-documents/smtp-status');
      if (!st.data.enabled || !st.data.configured) {
        alert('Mail gönderimi için SMTP ayarları yapılmamış.\n\nAyarlar → SMTP sekmesinden:\n• Sunucu adresi ve port\n• Kullanıcı adı / parola\n• Gönderen e-posta\nalanlarını doldurun ve "Aktif" işaretleyin.');
        return;
      }
      const to = window.prompt(`Alıcı e-posta adresi:\n(Gönderen: ${st.data.from_email || '-'})`, '');
      if (!to) return;
      if (!/.+@.+\..+/.test(to)) { alert('Geçersiz e-posta'); return; }
      const r = await api.post('/e-documents/inbox/' + row.id + '/email', { to });
      if (r.data.success) alert('E-posta başarıyla gönderildi:\n→ ' + to);
      else alert(r.data.message || 'Gönderilemedi');
    } catch (e) { alert(e.response?.data?.message || e.message); }
  };

  const pendingCount = list.filter(r => r.status === 'pending').length;

  return (
    <div className="stocks-page">
      <ListPageHeader
        title={title}
        unit="belge"
        count={list.length}
        showFilterButton
        filtersActive={showFilterBar}
        onToggleFilters={() => setShowFilterBar(s => !s)}
        onRefresh={load}
        refreshing={loading}
        actions={
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            {pendingCount > 0 && (
              <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 'var(--r-full)', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 700 }}>
                {pendingCount} Bekliyor
              </span>
            )}
            <Button size="sm" onClick={() => syncInbox(90)} loading={syncing}
              title="Hızlı Bilişim'den son 90 gün (~3 ay) içindeki gelen e-belgeleri çek">
              ↻ Son 90 Gün
            </Button>
            <Button size="sm" variant="ghost" onClick={() => syncInbox(365)} loading={syncing}
              title="Son 1 yıl — 50 günlük parçalar halinde tarama yapar">
              ↻ Son 1 Yıl
            </Button>
          </span>
        }
      />

      {syncResult && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 8, fontSize: '0.85rem',
          background: syncResult.type === 'error' ? 'rgba(220,38,38,0.08)' : 'rgba(22,163,74,0.08)',
          border: '1px solid ' + (syncResult.type === 'error' ? 'rgba(220,38,38,0.25)' : 'rgba(22,163,74,0.25)'),
          color: syncResult.type === 'error' ? 'var(--danger)' : 'var(--success)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}>
          <span>{syncResult.type === 'success' ? '✓' : '⚠'} {syncResult.text}</span>
          <button onClick={() => setSyncResult(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.7, fontSize: '0.9rem' }}>✕</button>
        </div>
      )}

      <FilterBar
        active={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'status', type: 'select', label: 'Durum', options: [
            { value: '', label: 'Tümü' }, ...Object.entries(STATUS_LABELS).map(([k,v]) => ({ value: k, label: v })),
          ]},
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey={`e-documents-in-${docGroup}`}
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Gelen belge yok"
          data={list}
          rowKey="id"
          onRowClick={openDetail}
          columns={[
            { id: 'docno_ettn', header: 'Fatura No / ETTN', accessor: 'document_number', sortable: true, width: 200,
              cell: r => (
                <div style={{ lineHeight: 1.4 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--accent-primary)' }}>{r.document_number || '-'}</div>
                  {r.document_uuid && (
                    <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}
                         title={r.document_uuid}>
                      {String(r.document_uuid).substring(0, 32)}...
                    </div>
                  )}
                </div>
              ) },
            { id: 'sender', header: 'Firma Adı', accessor: 'sender_name', sortable: true,
              cell: r => (
                <div style={{ lineHeight: 1.4 }}>
                  <div style={{ fontWeight: 500 }}>{r.sender_name || '-'}</div>
                  {r.sender_name?.startsWith('urn:') && (
                    <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>✉ {r.sender_name.replace(/^urn:mail:/, '')}</div>
                  )}
                </div>
              ) },
            { id: 'sender_vkn', header: 'Vergi Kimlik No', accessor: 'sender_vkn', sortable: true, width: 130,
              cell: r => <span style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{r.sender_vkn || '-'}</span> },
            { id: 'type_scenario', header: 'Fatura Tip / Senaryo', sortable: true, width: 140,
              accessor: r => (getInvoiceType(r) + ' / ' + getScenario(r)),
              cell: r => (
                <div style={{ lineHeight: 1.4, fontSize: '0.78rem' }}>
                  <div style={{ fontWeight: 600 }}>{getInvoiceType(r)}</div>
                  <div style={{ color: 'var(--text-tertiary)', fontSize: '0.72rem' }}>{getScenario(r)}</div>
                </div>
              ) },
            { id: 'docdate', header: 'Fatura Tarihi', accessor: 'document_date', sortable: true, width: 110,
              cell: r => <span style={{ fontSize: '0.82rem' }}>{fmtDateOnly(r.document_date)}</span> },
            { id: 'received', header: 'İşlem Tarihi', accessor: 'received_at', sortable: true, width: 140,
              cell: r => <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{fmtDate(getCreationDate(r))}</span> },
            { id: 'kdv', header: 'KDV', sortable: true, width: 110,
              accessor: r => getKdv(r) || 0,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => {
                const kdv = getKdv(r);
                return kdv != null ? <span style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{fmt(kdv)}</span> : <span style={{ color: 'var(--text-tertiary)' }}>-</span>;
              } },
            { id: 'total', header: 'Ödenecek Tutar', accessor: r => parseFloat(r.total_amount) || 0, sortable: true, width: 130,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(r.total_amount)} {r.currency_code || 'TRY'}</span> },
            { id: 'accounted', header: 'Muhasebe', sortable: true, width: 90,
              accessor: r => r.converted_purchase_invoice_id ? 'evet' : 'hayir',
              cellStyle: { textAlign: 'center' },
              cell: r => r.converted_purchase_invoice_id
                ? <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, background: 'rgba(16,185,129,0.15)', color: 'var(--success)', fontWeight: 600 }}>Evet</span>
                : <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>Hayır</span> },
            { id: 'status', header: 'Fatura Durumu', accessor: 'status', sortable: true, width: 130,
              cell: r => {
                const c = STATUS_COLORS[r.status] || {};
                return (
                  <div style={{ lineHeight: 1.4 }}>
                    <span style={{ fontSize: '0.7rem', padding: '3px 9px', borderRadius: 4, fontWeight: 700, background: c.bg, color: c.clr }}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                    {r.response_status && (
                      <div style={{ marginTop: 3, fontSize: 10, fontWeight: 600,
                        color: r.response_status === 'rejected' ? 'var(--danger)' : 'var(--success)' }}>
                        {r.response_status === 'rejected' ? 'Reddedildi' : 'Kabul Edildi'}
                      </div>
                    )}
                  </div>
                );
              } },
            { id: 'response', header: 'Yanıt', accessor: 'response_status', sortable: true, width: 100,
              cell: r => r.response_status ? <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: r.response_status === 'rejected' ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)', color: r.response_status === 'rejected' ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>{RESPONSE_LABELS[r.response_status]}</span> : <span className="stock-secondary">—</span> },
            { id: 'actions', header: 'İşlemler', sortable: false, hideable: false, draggable: false, filterable: false, resizable: false, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => (
                <span onClick={e => e.stopPropagation()}>
                  <ActionsMenu buttonLabel="Seçiniz ▾" align="right" items={[
                    // Görüntüleme + Yazdır + Mail
                    { label: 'Faturayı Göster', icon: '👁', onClick: () => viewInvoice(r), hidden: !r.document_uuid },
                    { label: 'Yazdır', icon: '🖨', onClick: () => printInvoice(r), hidden: !r.document_uuid },
                    { label: 'Mail Gönder', icon: '✉', onClick: () => emailInvoice(r), hidden: !r.document_uuid },
                    { separator: true, hidden: !r.document_uuid },
                    // İndirme
                    { label: 'PDF Olarak İndir', icon: '⬇', onClick: () => downloadFile(r, 'PDF'), hidden: !r.document_uuid },
                    { label: 'XML Olarak İndir', icon: '⬇', onClick: () => downloadFile(r, 'XML'), hidden: !r.document_uuid },
                    { label: 'HTML Olarak İndir', icon: '⬇', onClick: () => downloadFile(r, 'HTML'), hidden: !r.document_uuid },
                    { separator: true, hidden: !r.document_uuid },
                    // Detay
                    { label: 'Detayları Göster', icon: '📋', onClick: () => openDetail(r) },
                    { separator: true, hidden: r.status !== 'pending' || !!r.response_status },
                    // Kabul / Red
                    { label: 'Kabul Et', icon: '✓', variant: 'primary', onClick: () => respond(r, 'accepted'), hidden: r.status !== 'pending' || !!r.response_status },
                    { label: 'Reddet', icon: '✗', variant: 'danger', onClick: () => respond(r, 'rejected'), hidden: r.status !== 'pending' || !!r.response_status },
                    { separator: true, hidden: r.doc_type !== 'eFatura' },
                    // GİB itiraz / iade (sadece e-Fatura için anlamlı; e-Arşivde itiraz yapılmaz)
                    { label: 'İtiraz Et', icon: '⚠', variant: 'danger', onClick: () => objection(r, 'itiraz'),
                      hidden: r.doc_type !== 'eFatura', title: 'GİB sistemi üzerinden itiraz başvurusu' },
                    { label: 'İade Faturası Oluştur', icon: '↩', onClick: () => createReturnInvoice(r),
                      hidden: r.doc_type !== 'eFatura', title: 'Ters kayıtla iade faturası taslağı oluştur' },
                  ]} />
                </span>
              ) },
          ]}
        />
      </div>

      {viewerHtml && (
        <Modal title={`Fatura: ${viewerHtml.title}`} size="xl" onClose={() => setViewerHtml(null)}
               bodyStyle={{ padding: 0, display: 'flex' }}>
          <iframe srcDoc={viewerHtml.html} style={{ width: '100%', height: '90vh', border: 0, background: '#fff', display: 'block' }} title="Fatura" />
        </Modal>
      )}

      {detail && (
        <Modal title={`Gelen Belge · ${detail.document_number || ''}`} size="lg" onClose={() => setDetail(null)}
               cancelAction={{ label: 'Kapat', onClick: () => setDetail(null) }}
               primaryAction={detail.document_uuid ? { label: '📄 Faturayı Göster', onClick: () => { viewInvoice(detail); }, loading: viewerLoading } : null}>
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <div><strong>Tip:</strong> {DOC_TYPE_LABELS[detail.doc_type]}</div>
            <div><strong>Durum:</strong> {STATUS_LABELS[detail.status]}</div>
            <div><strong>Gönderen:</strong> {detail.sender_name} ({detail.sender_vkn})</div>
            <div><strong>UUID:</strong> <code style={{ fontSize: 11 }}>{detail.document_uuid || '-'}</code></div>
            <div><strong>ETTN:</strong> <code style={{ fontSize: 11 }}>{detail.ettn || '-'}</code></div>
            <div><strong>Tutar:</strong> {fmt(detail.total_amount)} {detail.currency_code}</div>
            <div><strong>Yanıt:</strong> {RESPONSE_LABELS[detail.response_status] || '-'} {detail.response_at ? `(${fmtDate(detail.response_at)})` : ''}</div>
            <div><strong>Faturaya Dönüştü:</strong> {detail.converted_purchase_invoice_id ? '✓' : '—'}</div>
          </div>
          {detail.payload_json && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}>JSON Payload (debug)</summary>
              <pre style={{ marginTop: 8, padding: 10, background: 'var(--bg-secondary)', borderRadius: 4, fontSize: 11, maxHeight: 400, overflow: 'auto' }}>
                {(() => { try { return JSON.stringify(JSON.parse(detail.payload_json), null, 2); } catch { return detail.payload_json; } })()}
              </pre>
            </details>
          )}
        </Modal>
      )}
    </div>
  );
}
